import { useEffect } from "react";

/**
 * Inject a JSON-LD <script> tag into the document head.
 *
 * Why a hook instead of dangerouslySetInnerHTML in JSX?
 *   - Keeps schema in <head> (where Google prefers it) rather than <body>.
 *   - Stable id-based dedupe means a remount or page transition replaces
 *     the existing tag instead of stacking duplicates.
 *
 * @param {object} data    Plain JSON-LD object (will be JSON.stringify'd).
 * @param {string} id      DOM id to dedupe by (e.g. "ld-faq-landing").
 */
export default function JsonLd({ data, id }) {
    useEffect(() => {
        if (!data || !id) return;
        const tagId = `jsonld-${id}`;
        let el = document.getElementById(tagId);
        if (!el) {
            el = document.createElement("script");
            el.type = "application/ld+json";
            el.id = tagId;
            document.head.appendChild(el);
        }
        try {
            el.textContent = JSON.stringify(data);
        } catch {
            /* ignore — never break the page over schema */
        }
        return () => {
            // Remove on unmount so per-page schema doesn't leak across routes.
            const stale = document.getElementById(tagId);
            if (stale && stale.parentNode) stale.parentNode.removeChild(stale);
        };
    }, [data, id]);

    return null;
}
